import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Phone, Calendar, FileText, CreditCard, Check,
  ChevronLeft, ChevronRight, AlertCircle, Loader2, Plane, Building2,
  Bus, Shield, Users
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useCurrency } from '../context/CurrencyContext';

const Checkout = ({ 
  searchParams,
  selectedPackage,
  onBack,
  onComplete
}) => {
  const { formatPrice } = useCurrency();
  const [step, setStep] = useState(1); // 1: Passengers, 2: Review, 3: Payment
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookingRef, setBookingRef] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  const passengers = (searchParams?.adults || 2) + (searchParams?.children || 0);
  const infants = searchParams?.infants || 0;
  const totalTravelers = passengers + infants;
  
  // Passenger form state
  const [passengerForms, setPassengerForms] = useState(
    Array(totalTravelers).fill(null).map((_, i) => ({
      title: i === 0 ? 'Mr' : '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      passportNumber: '',
      passportExpiry: '',
      nationality: 'British',
      email: i === 0 ? '' : undefined,
      phone: i === 0 ? '' : undefined,
      isLead: i === 0,
      type: i < (searchParams?.adults || 2) ? 'adult' : (i < passengers ? 'child' : 'infant')
    }))
  );
  
  const [paymentType, setPaymentType] = useState('deposit');
  
  // Calculate pricing
  const depositAmount = 50 * passengers;
  const totalAmount = selectedPackage?.pricing?.total || 0;
  
  // Update passenger form
  const updatePassenger = (index, field, value) => {
    setPassengerForms(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  // Validate passenger forms
  const validatePassengers = () => {
    for (let i = 0; i < passengerForms.length; i++) {
      const p = passengerForms[i];
      if (!p.title || !p.firstName || !p.lastName || !p.dateOfBirth || 
          !p.passportNumber || !p.passportExpiry) {
        return { valid: false, message: `Please complete all fields for Passenger ${i + 1}` };
      }
      if (p.isLead && (!p.email || !p.phone)) {
        return { valid: false, message: 'Please provide email and phone for lead passenger' };
      }
    }
    return { valid: true };
  };
  
  // Create booking
  const createBooking = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/payments/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_type: selectedPackage?.packageType || 'custom',
          passengers: passengerForms.map(p => ({
            title: p.title,
            first_name: p.firstName,
            last_name: p.lastName,
            date_of_birth: p.dateOfBirth,
            passport_number: p.passportNumber,
            passport_expiry: p.passportExpiry,
            nationality: p.nationality,
            email: p.email,
            phone: p.phone
          })),
          lead_passenger_email: passengerForms[0].email,
          lead_passenger_phone: passengerForms[0].phone,
          makkah_hotel_id: selectedPackage?.makkahHotel?.id,
          madinah_hotel_id: selectedPackage?.madinahHotel?.id,
          outbound_flight_id: selectedPackage?.outboundFlight?.id,
          return_flight_id: selectedPackage?.inboundFlight?.id,
          total_amount: totalAmount,
          deposit_amount: depositAmount,
          payment_type: paymentType,
          metadata: {
            search_params: searchParams,
            package_details: {
              makkah_hotel: selectedPackage?.makkahHotel?.name,
              madinah_hotel: selectedPackage?.madinahHotel?.name
            }
          }
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setBookingRef(data.booking_reference);
        setStep(3);
      } else {
        throw new Error(data.detail || 'Failed to create booking');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Initiate payment
  const initiatePayment = async () => {
    if (!bookingRef) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/payments/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_reference: bookingRef,
          payment_type: paymentType,
          origin_url: originUrl
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error(data.detail || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Check payment status (called after return from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const bookingRefFromUrl = urlParams.get('booking_ref');
    
    if (sessionId && bookingRefFromUrl) {
      setBookingRef(bookingRefFromUrl);
      pollPaymentStatus(sessionId);
    }
  }, []);
  
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setError('Payment verification timed out. Please contact support.');
      return;
    }
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/payments/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setStep(4);
      } else if (data.status === 'expired') {
        setError('Payment session expired. Please try again.');
      } else {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      setError('Error checking payment status');
    }
  };
  
  // Render Step 1: Passenger Details
  const renderPassengerDetails = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Users className="w-8 h-8 text-purple-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Passenger Details</h2>
          <p className="text-gray-500">Enter details for all {totalTravelers} traveler(s)</p>
        </div>
      </div>
      
      {passengerForms.map((passenger, index) => (
        <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-purple-600" />
            Passenger {index + 1}
            {passenger.isLead && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                Lead Passenger
              </span>
            )}
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full ml-auto">
              {passenger.type === 'adult' ? 'Adult' : passenger.type === 'child' ? 'Child' : 'Infant'}
            </span>
          </h3>
          
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Title *</Label>
              <select 
                value={passenger.title}
                onChange={(e) => updatePassenger(index, 'title', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                data-testid={`passenger-${index}-title`}
              >
                <option value="">Select</option>
                <option value="Mr">Mr</option>
                <option value="Mrs">Mrs</option>
                <option value="Ms">Ms</option>
                <option value="Miss">Miss</option>
                <option value="Dr">Dr</option>
              </select>
            </div>
            
            <div>
              <Label>First Name *</Label>
              <Input 
                value={passenger.firstName}
                onChange={(e) => updatePassenger(index, 'firstName', e.target.value)}
                placeholder="As per passport"
                className="mt-1"
                data-testid={`passenger-${index}-firstname`}
              />
            </div>
            
            <div>
              <Label>Last Name *</Label>
              <Input 
                value={passenger.lastName}
                onChange={(e) => updatePassenger(index, 'lastName', e.target.value)}
                placeholder="As per passport"
                className="mt-1"
                data-testid={`passenger-${index}-lastname`}
              />
            </div>
            
            <div>
              <Label>Date of Birth *</Label>
              <Input 
                type="date"
                value={passenger.dateOfBirth}
                onChange={(e) => updatePassenger(index, 'dateOfBirth', e.target.value)}
                className="mt-1"
                data-testid={`passenger-${index}-dob`}
              />
            </div>
            
            <div>
              <Label>Passport Number *</Label>
              <Input 
                value={passenger.passportNumber}
                onChange={(e) => updatePassenger(index, 'passportNumber', e.target.value)}
                placeholder="Enter passport number"
                className="mt-1"
                data-testid={`passenger-${index}-passport`}
              />
            </div>
            
            <div>
              <Label>Passport Expiry *</Label>
              <Input 
                type="date"
                value={passenger.passportExpiry}
                onChange={(e) => updatePassenger(index, 'passportExpiry', e.target.value)}
                className="mt-1"
                data-testid={`passenger-${index}-passport-expiry`}
              />
            </div>
            
            <div>
              <Label>Nationality</Label>
              <select 
                value={passenger.nationality}
                onChange={(e) => updatePassenger(index, 'nationality', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="British">British</option>
                <option value="Irish">Irish</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Pakistani">Pakistani</option>
                <option value="Indian">Indian</option>
                <option value="Bangladeshi">Bangladeshi</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            {passenger.isLead && (
              <>
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={passenger.email || ''}
                    onChange={(e) => updatePassenger(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    className="mt-1"
                    data-testid="lead-email"
                  />
                </div>
                
                <div>
                  <Label>Phone *</Label>
                  <Input 
                    type="tel"
                    value={passenger.phone || ''}
                    onChange={(e) => updatePassenger(index, 'phone', e.target.value)}
                    placeholder="+44 7xxx xxx xxx"
                    className="mt-1"
                    data-testid="lead-phone"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
  
  // Render Step 2: Review Booking
  const renderReview = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <FileText className="w-8 h-8 text-purple-600" />
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Review Your Booking</h2>
          <p className="text-gray-500">Please confirm all details before proceeding</p>
        </div>
      </div>
      
      {/* Package Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-bold text-lg text-gray-800 mb-4">Package Summary</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Flight Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-gray-700">
              <Plane className="w-5 h-5 text-purple-600" />
              <span className="font-medium">Flights</span>
            </div>
            <div className="pl-7 text-sm text-gray-600">
              <p>Outbound: {selectedPackage?.outboundFlight?.validating_carrier || 'SV'}</p>
              <p>Return: {selectedPackage?.inboundFlight?.validating_carrier || 'SV'}</p>
              <p>{searchParams?.departureDate} - {searchParams?.returnDate}</p>
            </div>
          </div>
          
          {/* Hotels */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-gray-700">
              <Building2 className="w-5 h-5 text-purple-600" />
              <span className="font-medium">Hotels</span>
            </div>
            <div className="pl-7 text-sm text-gray-600">
              <p><strong>Makkah:</strong> {selectedPackage?.makkahHotel?.name || 'Selected'}</p>
              <p>{searchParams?.makkahNights} nights</p>
              <p><strong>Madinah:</strong> {selectedPackage?.madinahHotel?.name || 'Selected'}</p>
              <p>{searchParams?.madinahNights} nights</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Passengers Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-bold text-lg text-gray-800 mb-4">Passengers ({totalTravelers})</h3>
        <div className="space-y-2">
          {passengerForms.map((p, i) => (
            <div key={i} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
              <span className="text-gray-800">{p.title} {p.firstName} {p.lastName}</span>
              <span className="text-gray-500 text-sm">{p.type}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Price Breakdown */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
        <h3 className="font-bold text-lg text-gray-800 mb-4">Price Breakdown</h3>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span>Flights</span>
            <span>£{(selectedPackage?.pricing?.flights || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Hotels</span>
            <span>£{(selectedPackage?.pricing?.hotels || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span>Transport</span>
            <span>£{(selectedPackage?.pricing?.transport || 0).toFixed(2)}</span>
          </div>
          {selectedPackage?.pricing?.visa > 0 && (
            <div className="flex justify-between">
              <span>Visa</span>
              <span>£{selectedPackage.pricing.visa.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between pt-2 border-t border-purple-300 font-bold text-lg">
            <span>Total</span>
            <span className="text-purple-700">£{totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>
      
      {/* Payment Options */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="font-bold text-lg text-gray-800 mb-4">Payment Option</h3>
        <div className="space-y-3">
          <label className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentType === 'deposit' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
          }`}>
            <input 
              type="radio" 
              name="paymentType" 
              value="deposit"
              checked={paymentType === 'deposit'}
              onChange={(e) => setPaymentType(e.target.value)}
              className="mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-800">Pay Deposit Only</div>
              <div className="text-sm text-gray-500">£50 per person • Balance due before travel</div>
            </div>
            <div className="text-green-600 font-bold">£{depositAmount.toFixed(2)}</div>
          </label>
          
          <label className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all ${
            paymentType === 'full' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
          }`}>
            <input 
              type="radio" 
              name="paymentType" 
              value="full"
              checked={paymentType === 'full'}
              onChange={(e) => setPaymentType(e.target.value)}
              className="mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-800">Pay in Full</div>
              <div className="text-sm text-gray-500">Secure your booking with full payment</div>
            </div>
            <div className="text-purple-600 font-bold">£{totalAmount.toFixed(2)}</div>
          </label>
        </div>
      </div>
    </div>
  );
  
  // Render Step 3: Payment
  const renderPayment = () => (
    <div className="space-y-6 text-center">
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 max-w-md mx-auto">
        <CreditCard className="w-16 h-16 text-purple-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Ready to Pay</h2>
        <p className="text-gray-500 mb-6">
          Booking Reference: <span className="font-mono font-bold text-purple-600">{bookingRef}</span>
        </p>
        
        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <div className="text-3xl font-bold text-purple-700">
            £{(paymentType === 'deposit' ? depositAmount : totalAmount).toFixed(2)}
          </div>
          <div className="text-sm text-purple-600">
            {paymentType === 'deposit' ? 'Deposit Payment' : 'Full Payment'}
          </div>
        </div>
        
        <Button
          onClick={initiatePayment}
          disabled={loading}
          className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl"
          data-testid="pay-now-btn"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Processing...</>
          ) : (
            <><CreditCard className="w-5 h-5 mr-2" /> Pay Securely with Stripe</>
          )}
        </Button>
        
        <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
          <Shield className="w-4 h-4" />
          Secure payment powered by Stripe
        </div>
      </div>
    </div>
  );
  
  // Download PDF document
  const downloadPDF = (docType) => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    let url = '';
    
    switch(docType) {
      case 'confirmation':
        url = `${API_URL}/api/payments/booking/${bookingRef}/pdf/confirmation`;
        break;
      case 'invoice':
        url = `${API_URL}/api/payments/booking/${bookingRef}/pdf/invoice`;
        break;
      case 'makkah_voucher':
        url = `${API_URL}/api/payments/booking/${bookingRef}/pdf/voucher/makkah`;
        break;
      case 'madinah_voucher':
        url = `${API_URL}/api/payments/booking/${bookingRef}/pdf/voucher/madinah`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank');
  };

  // Render Step 4: Success
  const renderSuccess = () => (
    <div className="space-y-6 text-center">
      <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 max-w-lg mx-auto">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
        <p className="text-gray-500 mb-6">
          Thank you for your booking. Your reference number is:
        </p>
        <div className="bg-purple-50 rounded-lg p-4 mb-6">
          <div className="text-2xl font-mono font-bold text-purple-700">{bookingRef}</div>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          A confirmation email has been sent to {passengerForms[0]?.email}
        </p>
        
        {/* Download Documents */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center justify-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            Download Documents
          </h3>
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPDF('confirmation')}
              className="text-sm"
              data-testid="download-confirmation-btn"
            >
              📄 Confirmation
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPDF('invoice')}
              className="text-sm"
              data-testid="download-invoice-btn"
            >
              🧾 Invoice
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPDF('makkah_voucher')}
              className="text-sm"
              data-testid="download-makkah-voucher-btn"
            >
              🕋 Makkah Voucher
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadPDF('madinah_voucher')}
              className="text-sm"
              data-testid="download-madinah-voucher-btn"
            >
              🕌 Madinah Voucher
            </Button>
          </div>
        </div>
        
        <Button
          onClick={() => window.location.href = '/'}
          className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl"
        >
          Return to Homepage
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold mb-2">Checkout</h1>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-4">
            {[
              { num: 1, label: 'Passengers' },
              { num: 2, label: 'Review' },
              { num: 3, label: 'Payment' },
              { num: 4, label: 'Complete' }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className={`flex items-center gap-2 ${step >= s.num ? 'text-white' : 'text-purple-300'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step > s.num ? 'bg-green-500' : step === s.num ? 'bg-white text-purple-600' : 'bg-purple-500'
                  }`}>
                    {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                  </div>
                  <span className="hidden sm:inline text-sm">{s.label}</span>
                </div>
                {i < 3 && <div className={`flex-1 h-1 ${step > s.num ? 'bg-green-500' : 'bg-purple-500'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-700">{error}</p>
          </div>
        )}
        
        {step === 1 && renderPassengerDetails()}
        {step === 2 && renderReview()}
        {step === 3 && renderPayment()}
        {step === 4 && renderSuccess()}
        
        {/* Navigation Buttons */}
        {step < 4 && (
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => step === 1 ? onBack() : setStep(step - 1)}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              {step === 1 ? 'Back to Package' : 'Back'}
            </Button>
            
            {step < 3 && (
              <Button
                onClick={() => {
                  if (step === 1) {
                    const validation = validatePassengers();
                    if (!validation.valid) {
                      setError(validation.message);
                      return;
                    }
                    setError(null);
                    setStep(2);
                  } else if (step === 2) {
                    createBooking();
                  }
                }}
                disabled={loading}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
                data-testid="continue-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Processing</>
                ) : (
                  <>
                    {step === 2 ? 'Proceed to Payment' : 'Continue'}
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkout;
